// `/api/auth/$` is the framework-native handler surface for every Better
// Auth HTTP path. The GET handler delegates everything to
// `authService.handleRequest` (which dispatches into Better Auth's bundled
// `auth.handler`); the POST handler short-circuits on the two paths that
// participate in our audit-log + observability flow (sign-in, sign-out) and
// passes the rest through to the same `authService.handleRequest`.
//
// The `signIn` / `signOut` application use cases fire audit + observability
// off a `SignInResult` / sign-out completion that the adapter produces
// alongside the cookie-bearing Better Auth Response. The route handler is
// the single place where the adapter, the tenancy scope, and the use case
// come together for an HTTP request — `server-entry.ts` no longer carries
// an `/api/auth` short-circuit.

import { createFileRoute } from '@tanstack/react-router';
import { getServerEnv } from '~/_shared/config/env.server';
import { getServerObservability } from '~/_shared/observability/composition.server';
import {
	getServerTenancyAuditLogRepository,
	getServerTenancyService,
} from '~/_shared/tenancy/composition.server';
import { signIn } from '~/bounded-contexts/authentication/application/sign-in';
import { signOut } from '~/bounded-contexts/authentication/application/sign-out';
import { getServerAuthService } from '~/bounded-contexts/authentication/composition.server';

async function runSignIn(request: Request): Promise<Response> {
	const env = getServerEnv();
	const authService = getServerAuthService();
	const tenancy = getServerTenancyService();
	const auditLog = getServerTenancyAuditLogRepository();
	const observability = await getServerObservability(env);

	let body: { username: string; password: string };
	try {
		body = (await request.clone().json()) as {
			username: string;
			password: string;
		};
	} catch {
		return new Response(JSON.stringify({ error: 'invalid-body' }), {
			status: 400,
			headers: { 'content-type': 'application/json' },
		});
	}

	const { result, response } = await authService.signInWithUsernameAndResponse(
		body,
		request,
	);
	await signIn({ auditLog, tenancy, observability }, result);
	return response;
}

async function runSignOut(request: Request): Promise<Response> {
	const env = getServerEnv();
	const authService = getServerAuthService();
	const tenancy = getServerTenancyService();
	const auditLog = getServerTenancyAuditLogRepository();
	const observability = await getServerObservability(env);

	// Resolve scope BEFORE signing out — once the session is invalidated we
	// can't read userId/sessionId/clubId off of it.
	const scope = await authService.resolveTenantScope(request);
	const response = await authService.signOutAndResponse(request);

	if (scope) {
		await tenancy.withTenant(scope.clubId, () =>
			signOut(
				{ auditLog, observability },
				{
					userId: scope.session.userId,
					sessionId: scope.session.id,
				},
			),
		);
	}

	return response;
}

export const Route = createFileRoute('/api/auth/$')({
	server: {
		handlers: {
			GET: ({ request }) => getServerAuthService().handleRequest(request),
			POST: async ({ request }) => {
				const pathname = new URL(request.url).pathname;
				if (pathname === '/api/auth/sign-in/username') {
					return runSignIn(request);
				}
				if (pathname === '/api/auth/sign-out') {
					return runSignOut(request);
				}
				return getServerAuthService().handleRequest(request);
			},
		},
	},
	component: () => null,
});
